import { env, debugConfig } from "@/config/env";

/**
 * Centralized logging utility that respects environment configuration
 * Only logs in development environment to avoid console warnings in production
 */
export const logger = {
  /**
   * Log informational messages (only in development)
   */
  log: (...args: unknown[]) => {
    if (debugConfig.enableLogging) {
      // eslint-disable-next-line no-console
      console.log(...args);
    }
  },

  /**
   * Log warning messages (only in development)
   */
  warn: (...args: unknown[]) => {
    if (debugConfig.enableLogging) {
      // eslint-disable-next-line no-console
      console.warn(...args);
    }
  },

  /**
   * Log error messages (only in development to avoid console errors in production)
   */
  error: (...args: unknown[]) => {
    if (debugConfig.enableLogging) {
      // eslint-disable-next-line no-console
      console.error(...args);
    }
  },

  /**
   * Log critical errors that should always be shown (use sparingly)
   */
  criticalError: (...args: unknown[]) => {
    // eslint-disable-next-line no-console
    console.error(...args);
  },

  /**
   * Log grouped messages (only in development)
   */
  group: (...args: unknown[]) => {
    if (debugConfig.enableLogging) {
      // eslint-disable-next-line no-console
      console.group(...args);
    }
  },

  /**
   * End grouped messages (only in development)
   */
  groupEnd: () => {
    if (debugConfig.enableLogging) {
      // eslint-disable-next-line no-console
      console.groupEnd();
    }
  },

  /**
   * Log debug information with environment context
   */
  debug: (context: string, data: unknown) => {
    if (debugConfig.enableLogging) {
      // eslint-disable-next-line no-console
      console.log(`🐛 [${context}]`, data);
    }
  },

  /**
   * Log API requests and responses (only in development)
   */
  api: {
    request: (method: string, url: string, data?: unknown) => {
      if (debugConfig.enableApiLogging) {
        // eslint-disable-next-line no-console
        console.log(`📡 API ${method}:`, url, data ? { data } : "");
      }
    },

    response: (method: string, url: string, response: unknown) => {
      if (debugConfig.enableApiLogging) {
        // eslint-disable-next-line no-console
        console.log(`✅ API ${method} Response:`, url, response);
      }
    },

    error: (method: string, url: string, error: unknown) => {
      if (debugConfig.enableApiLogging) {
        // eslint-disable-next-line no-console
        console.error(`❌ API ${method} Error:`, url, error);
      }
    },
  },
};

// Environment info logging (only in development)
if (env.IS_DEVELOPMENT) {
  logger.group("🌍 Environment Configuration");
  logger.log("Environment:", env.INFRAGENTS_ENV);
  logger.log("API Base URL:", env.API_BASE_URL);
  logger.log("Maintenance Mode:", env.MAINTENANCE_MODE);
  logger.log("Debug Logging:", debugConfig.enableLogging);
  logger.groupEnd();
}
